import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import { Store } from '../services/storeService';
import { Chain } from '../services/chainService';
import WeeklyWorkSchedule, { WeeklyWorkScheduleValue } from './WeeklyWorkSchedule';

interface AddStoreToProgramDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: { store_id: number; visit_time_minutes: number; visiting_hours: any; rate: number }) => void;
  stores: Store[];
  chains: Chain[];
  planningDays?: number; // <-- add this prop
  initialValues?: {
    store_id: number;
    visit_time_minutes?: number;
    visiting_hours?: any;
    rate?: number;
  };
}

const AddStoreToProgramDialog: React.FC<AddStoreToProgramDialogProps> = ({ open, onClose, onAdd, stores, chains, initialValues }) => {
  const [chainFilter, setChainFilter] = useState<number>(0);
  const [storeId, setStoreId] = useState<number>(initialValues?.store_id || 0);
  const [visitTime, setVisitTime] = useState<number>(initialValues?.visit_time_minutes ?? 0);
  const [visitingHours, setVisitingHours] = useState<WeeklyWorkScheduleValue>(
    (initialValues?.visiting_hours && 'weekly' in initialValues.visiting_hours)
      ? (initialValues.visiting_hours as WeeklyWorkScheduleValue)
      : { weekly: {}, holidays: [] }
  );
  const [planType, setPlanType] = useState<'detailed' | 'visits_per_week'>(
    initialValues?.visiting_hours && 'simple' in initialValues.visiting_hours ? 'visits_per_week' : 'detailed'
  );
  const [simpleWeekly, setSimpleWeekly] = useState<number>(
    initialValues?.visiting_hours && 'simple' in initialValues.visiting_hours ? initialValues.visiting_hours.simple.weekly : 1
  );
  const [simpleMonthly, setSimpleMonthly] = useState<number>(
    initialValues?.visiting_hours && 'simple' in initialValues.visiting_hours ? initialValues.visiting_hours.simple.monthly : 4
  );
  const [rate, setRate] = useState<number>(initialValues?.rate ?? 0);
  // Remove minDaysBetween, maxDaysBetween, minDaysManual, maxDaysManual
  // Instead, extract from initialValues.visiting_hours?.limits if present
  const [minDaysBetween, setMinDaysBetween] = useState<number>(initialValues?.visiting_hours?.limits?.min_days_between ?? 1);
  const [maxDaysBetween, setMaxDaysBetween] = useState<number>(initialValues?.visiting_hours?.limits?.max_days_between ?? 30);
  // Track if user has manually edited simpleWeekly
  const [_, setSimpleWeeklyManual] = useState(false);

  React.useEffect(() => {
    if (initialValues) {
      setStoreId(initialValues.store_id || 0);
      setVisitTime(initialValues.visit_time_minutes ?? 0);
      setRate(initialValues.rate ?? 0);
      if (initialValues.visiting_hours && 'limits' in initialValues.visiting_hours) {
        setMinDaysBetween(initialValues.visiting_hours.limits.min_days_between);
        setMaxDaysBetween(initialValues.visiting_hours.limits.max_days_between);
      }
      if (initialValues.visiting_hours && 'simple' in initialValues.visiting_hours) {
        setPlanType('visits_per_week');
        setSimpleWeekly(initialValues.visiting_hours.simple.weekly);
        setSimpleMonthly(initialValues.visiting_hours.simple.monthly);
      } else if (initialValues.visiting_hours && 'weekly' in initialValues.visiting_hours) {
        setPlanType('detailed');
        setVisitingHours(initialValues.visiting_hours as WeeklyWorkScheduleValue);
      }
    } else {
      setStoreId(0);
      setVisitTime(0);
      setVisitingHours({ weekly: {}, holidays: [] });
      setSimpleWeekly(1);
      setSimpleMonthly(4);
      setPlanType('detailed');
      setRate(0);
      setMinDaysBetween(1);
      setMaxDaysBetween(30);
    }
  }, [initialValues, open]);

  // Auto-calculate min/max days when monthly changes, unless manually overridden
  React.useEffect(() => {
    if (planType === 'visits_per_week') {
      const monthly = simpleMonthly;
      if (monthly && monthly > 0) {
        const minDays = Math.max(1, Math.floor(30 / monthly));
        const maxDays = Math.min(30, minDays + Math.max(1, Math.floor(minDays / 2)));
        setMinDaysBetween(minDays);
        setMaxDaysBetween(maxDays);
      } else {
        setMinDaysBetween(30);
        setMaxDaysBetween(30);
      }
    }
  }, [simpleMonthly, planType]);

  // Auto-update simpleWeekly when simpleMonthly changes, unless manually overridden
  React.useEffect(() => {
    if (planType === 'visits_per_week') {
      const monthly = simpleMonthly;
      if (monthly && monthly > 0) {
        setSimpleWeekly(Math.round(monthly / 4.33));
      }
    }
  }, [simpleMonthly, planType]);

  // Update visits when planningDays changes

  // When user edits min/max days, set manual override flags
  const handleMinDaysChange = (val: number) => {
    setMinDaysBetween(val);
  };
  const handleMaxDaysChange = (val: number) => {
    setMaxDaysBetween(val);
  };

  // When user edits simpleWeekly, set manual override flag
  const handleSimpleWeeklyChange = (val: number) => {
    setSimpleWeekly(val);
    setSimpleWeeklyManual(true);
  };

  // Reset manual flags if plan type or dialog is reset
  React.useEffect(() => {
    setSimpleWeeklyManual(false);
  }, [planType, open]);

  const filteredStores = chainFilter ? stores.filter(s => s.chain_id === chainFilter) : stores;

  // When calling onAdd, put min/max into visiting_hours.limits
  const handleAdd = () => {
    if (storeId) {
      let visitingHoursToSend: any;
      if (planType === 'visits_per_week') {
        visitingHoursToSend = { simple: { weekly: simpleWeekly, monthly: simpleMonthly }, limits: { min_days_between: minDaysBetween, max_days_between: maxDaysBetween } };
      } else {
        visitingHoursToSend = { ...visitingHours, limits: { min_days_between: minDaysBetween, max_days_between: maxDaysBetween } };
      }
      onAdd({ store_id: storeId, visit_time_minutes: visitTime, visiting_hours: visitingHoursToSend, rate });
      setStoreId(0);
      setVisitTime(0);
      setVisitingHours({ weekly: {}, holidays: [] });
      setSimpleWeekly(1);
      setSimpleMonthly(4);
      setPlanType('detailed');
      setRate(0);
      setMinDaysBetween(1);
      setMaxDaysBetween(30);
    }
  };

  const handleCancel = () => {
    setStoreId(0);
    setVisitTime(0);
    setVisitingHours({ weekly: {}, holidays: [] });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialValues ? 'Edit Store in Program' : 'Add Store to Program'}</DialogTitle>
      <DialogContent>
        <TextField
          select
          label="Chain Filter"
          value={chainFilter}
          onChange={e => setChainFilter(Number(e.target.value))}
          fullWidth
          style={{ marginBottom: 8 }}
        >
          <MenuItem value={0}>All Chains</MenuItem>
          {chains.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Store"
          value={storeId}
          onChange={e => setStoreId(Number(e.target.value))}
          fullWidth
          style={{ marginBottom: 8 }}
          disabled={!!initialValues}
        >
          {filteredStores.map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.address}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Visit Time (min)"
          type="number"
          value={visitTime}
          onChange={e => setVisitTime(Number(e.target.value))}
          fullWidth
          style={{ marginBottom: 8 }}
        />
        <TextField
          label="Rate"
          type="number"
          value={rate}
          onChange={e => setRate(Number(e.target.value))}
          fullWidth
          style={{ marginBottom: 8 }}
          inputProps={{ min: 0 }}
        />
        <TextField
          label="Min Days Between Visits"
          type="number"
          value={minDaysBetween}
          onChange={e => handleMinDaysChange(Math.max(1, Math.min(30, Number(e.target.value))))}
          fullWidth
          style={{ marginBottom: 8 }}
          inputProps={{ min: 1, max: 30 }}
        />
        <TextField
          label="Max Days Between Visits"
          type="number"
          value={maxDaysBetween}
          onChange={e => handleMaxDaysChange(Math.max(minDaysBetween, Math.min(30, Number(e.target.value))))}
          fullWidth
          style={{ marginBottom: 8 }}
          inputProps={{ min: minDaysBetween, max: 30 }}
        />
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Plan Type</FormLabel>
          <RadioGroup
            row
            value={planType}
            onChange={e => setPlanType(e.target.value as 'detailed' | 'visits_per_week')}
          >
            <FormControlLabel value="detailed" control={<Radio />} label="Detailed Schedule" />
            <FormControlLabel value="visits_per_week" control={<Radio />} label="Visits per Week" />
          </RadioGroup>
        </FormControl>
        {planType === 'visits_per_week' ? (
          <>
            <TextField
              label="Visits per Week"
              type="number"
              value={simpleWeekly}
              onChange={e => handleSimpleWeeklyChange(Number(e.target.value))}
              fullWidth
              style={{ marginBottom: 8 }}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Visits per Month"
              type="number"
              value={simpleMonthly}
              onChange={e => setSimpleMonthly(Number(e.target.value))}
              fullWidth
              style={{ marginBottom: 8 }}
              inputProps={{ min: 1 }}
            />
          </>
        ) : (
          <Box sx={{ minWidth: 300, flex: 1 }}>
            <WeeklyWorkSchedule
              value={visitingHours}
              onChange={setVisitingHours}
              holidayMode="single"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleAdd} variant="contained">{initialValues ? 'Save' : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStoreToProgramDialog; 